<script setup lang="ts">
    import { watch } from 'vue';
    import { useXrayUploadViewModel } from '@/views/case-settings/upload/xray/useXrayUploadViewModel';
    import DicomMismatchModal from '@/views/case-settings/upload/shared/DicomMismatchModal.vue';
    import XRayUploadProgress from '@/views/case-settings/upload/xray/components/XRayUploadProgress.vue';
    import { useCaseSyncStore } from '@/stores/useCaseSyncStore';

    const caseSync = useCaseSyncStore();
    const vm = useXrayUploadViewModel();

    watch(() => vm.hasUploaded, async (hasUploaded) => {
        if (hasUploaded) {
            await caseSync.refresh();
        }
    });
</script>

<template>
    <div v-if="vm.hasEnteredPatientData && !vm.isCaseDeemedUnsuitable">
        <v-file-upload v-model="vm.apFile" class="ma-4" clearable>
            <template #icon>
                <v-icon :color="vm.apFile.length > 0 ? 'success' : 'grey'">
                    {{ vm.apFile.length > 0 ? 'mdi-check-circle' : 'mdi-cloud-upload' }}
                </v-icon>
            </template>
            <template #title>
                {{ vm.apFile.length > 0 ? vm.apFile[0].name : 'AP X-ray' }}
            </template>
            <template #divider></template>
            <template #browse="{ props }">
                <app-button @click="props.onClick">
                    {{ vm.apFile.length > 0 ? 'Upload New X-ray' : 'Click to Upload' }}
                </app-button>
            </template>
            <template #item></template>
        </v-file-upload>

        <v-file-upload v-model="vm.leftObliqueFile" class="ma-4" clearable>
            <template #icon>
                <v-icon :color="vm.leftObliqueFile.length > 0 ? 'success' : 'grey'">
                    {{ vm.leftObliqueFile.length > 0 ? 'mdi-check-circle' : 'mdi-cloud-upload' }}
                </v-icon>
            </template>
            <template #title>
                {{ vm.leftObliqueFile.length > 0 ? vm.leftObliqueFile[0].name : 'Left-Oblique X-ray' }}
            </template>
            <template #divider></template>
            <template #browse="{ props }">
                <app-button @click="props.onClick">
                    {{ vm.leftObliqueFile.length > 0 ? 'Upload New X-ray' : 'Click to Upload' }}
                </app-button>
            </template>
            <template #item></template>
        </v-file-upload>

        <v-file-upload v-model="vm.rightObliqueFile" class="ma-4" clearable>
            <template #icon>
                <v-icon :color="vm.rightObliqueFile.length > 0 ? 'success' : 'grey'">
                    {{ vm.rightObliqueFile.length > 0 ? 'mdi-check-circle' : 'mdi-cloud-upload' }}
                </v-icon>
            </template>
            <template #title>
                {{ vm.rightObliqueFile.length > 0 ? vm.rightObliqueFile[0].name : 'Right-Oblique X-ray' }}
            </template>
            <template #divider></template>
            <template #browse="{ props }">
                <app-button @click="props.onClick">
                    {{ vm.rightObliqueFile.length > 0 ? 'Upload New X-ray' : 'Click to Upload' }}
                </app-button>
            </template>
            <template #item></template>
        </v-file-upload>

        <app-button
            class="ma-4 float-end"
            :disabled="!vm.canStartProcessing"
            @click="vm.uploadAndStartProcessing"
        >
            Start Processing
        </app-button>
    </div>

    <app-form-subtitle v-else-if="!vm.isCaseDeemedUnsuitable">
        Please enter all patient details, then return to this section to upload your X-rays.
    </app-form-subtitle>

    <div v-if="vm.hasUploaded">
        <v-alert
            type="success"
            title="Your files were successfully uploaded"
            text="We will process your case now. Please return to the case list to track the progress of your case." />
    </div>

    <DicomMismatchModal upload-type="xray-2d-3d" />
    <XRayUploadProgress />
</template>
